'use client'

import { useState, useEffect, forwardRef } from 'react'
import { Info, Ruler } from 'lucide-react'
import { cn } from '../utils/styles'

export interface MeasurementsInputProps {
  bust?: number
  waist?: number
  hips?: number
  height?: number
  onChange?: (measurements: {
    bust?: number
    waist?: number
    hips?: number
    height?: number
  }) => void
  errors?: {
    bust?: string
    waist?: string
    hips?: string
    height?: string
  }
  unit?: 'cm' | 'inch'
  onUnitChange?: (unit: 'cm' | 'inch') => void
  className?: string
  required?: boolean
  disabled?: boolean
  showCard?: boolean
  measurements?: Array<'bust' | 'waist' | 'hips' | 'height'>
}

export interface MeasurementField {
  key: 'bust' | 'waist' | 'hips' | 'height'
  label: string
  guide: string
  placeholder: string
}

const DEFAULT_MEASUREMENTS: MeasurementField[] = [
  {
    key: 'bust',
    label: 'Bust',
    guide: 'Measure around the fullest part of your bust, keeping the tape parallel to the ground.',
    placeholder: '80-120'
  },
  {
    key: 'waist',
    label: 'Waist',
    guide: 'Measure around your natural waistline, keeping the tape comfortably loose.',
    placeholder: '60-100'
  },
  {
    key: 'hips',
    label: 'Hips',
    guide: 'Measure around the fullest part of your hips, keeping the tape parallel to the ground.',
    placeholder: '85-130'
  },
  {
    key: 'height',
    label: 'Height',
    guide: 'Stand straight against a wall and measure from the floor to the top of your head.',
    placeholder: '150-200'
  }
]

export const MeasurementsInput = forwardRef<HTMLDivElement, MeasurementsInputProps>(({
  bust,
  waist,
  hips,
  height,
  onChange,
  errors = {},
  unit = 'cm',
  onUnitChange,
  className,
  required = false,
  disabled = false,
  showCard = true,
  measurements = ['bust', 'waist', 'hips', 'height'],
  ...props
}, ref) => {
  const [localUnit, setLocalUnit] = useState<'cm' | 'inch'>(unit)
  const [localValues, setLocalValues] = useState({
    bust: bust || '',
    waist: waist || '',
    hips: hips || '',
    height: height || '',
  })

  // Conversion functions
  const cmToInch = (cm: number) => Math.round((cm / 2.54) * 10) / 10
  const inchToCm = (inch: number) => Math.round(inch * 2.54)

  // Convert values when unit changes
  useEffect(() => {
    if (localUnit === 'inch') {
      setLocalValues({
        bust: bust ? String(cmToInch(bust)) : '',
        waist: waist ? String(cmToInch(waist)) : '',
        hips: hips ? String(cmToInch(hips)) : '',
        height: height ? String(cmToInch(height)) : '',
      })
    } else {
      setLocalValues({
        bust: bust ? String(bust) : '',
        waist: waist ? String(waist) : '',
        hips: hips ? String(hips) : '',
        height: height ? String(height) : '',
      })
    }
  }, [localUnit, bust, waist, hips, height])

  const handleChange = (field: keyof typeof localValues, value: string) => {
    const numValue = parseFloat(value)
    if (value === '' || isNaN(numValue)) {
      setLocalValues((prev) => ({ ...prev, [field]: value }))
      onChange?.({ ...{ bust, waist, hips, height }, [field]: undefined })
      return
    }

    setLocalValues((prev) => ({ ...prev, [field]: value }))

    // Convert to cm for storage
    const cmValue = localUnit === 'inch' ? inchToCm(numValue) : numValue
    onChange?.({ ...{ bust, waist, hips, height }, [field]: cmValue })
  }

  const getValidationRange = (field: string) => {
    const ranges = {
      cm: {
        bust: { min: 60, max: 150 },
        waist: { min: 50, max: 130 },
        hips: { min: 70, max: 150 },
        height: { min: 140, max: 220 },
      },
      inch: {
        bust: { min: 24, max: 59 },
        waist: { min: 20, max: 51 },
        hips: { min: 28, max: 59 },
        height: { min: 55, max: 87 },
      },
    }
    return ranges[localUnit][field as keyof typeof ranges.cm]
  }

  const handleUnitChange = (newUnit: 'cm' | 'inch') => {
    setLocalUnit(newUnit)
    onUnitChange?.(newUnit)
  }

  const renderMeasurementInput = (measurement: MeasurementField) => {
    const range = getValidationRange(measurement.key)
    return (
      <div key={measurement.key} className="space-y-2">
        <label htmlFor={measurement.key} className="flex items-center gap-2 text-sm font-medium">
          {measurement.label}
          {required && <span className="text-red-500">*</span>}
          <div className="relative group">
            <Info className="h-3 w-3 cursor-help text-gray-500" />
            <div className="absolute left-0 top-6 z-10 hidden group-hover:block w-64 p-2 text-xs text-white bg-black rounded shadow-lg">
              {measurement.guide}
            </div>
          </div>
        </label>
        <div className="relative">
          <input
            id={measurement.key}
            type="number"
            step="0.1"
            value={localValues[measurement.key]}
            onChange={(e) => handleChange(measurement.key, e.target.value)}
            placeholder={`e.g., ${range.min}-${range.max}`}
            disabled={disabled}
            className={cn(
              'flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 pr-12 text-sm ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50',
              errors[measurement.key] && 'border-red-500'
            )}
          />
          <span className="absolute right-3 top-1/2 -translate-y-1/2 text-sm text-gray-500">
            {localUnit}
          </span>
        </div>
        {errors[measurement.key] && (
          <p className="text-sm text-red-500">{errors[measurement.key]}</p>
        )}
      </div>
    )
  }

  const filteredMeasurements = DEFAULT_MEASUREMENTS.filter(m => measurements.includes(m.key))

  const content = (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Ruler className="h-5 w-5" />
          <h3 className="text-lg font-semibold">Body Measurements</h3>
        </div>
        <div className="flex rounded-md border">
          <button
            type="button"
            onClick={() => handleUnitChange('cm')}
            className={cn(
              'px-3 py-1 text-sm rounded-l-md',
              localUnit === 'cm'
                ? 'bg-primary text-primary-foreground'
                : 'bg-background text-foreground hover:bg-gray-100'
            )}
          >
            cm
          </button>
          <button
            type="button"
            onClick={() => handleUnitChange('inch')}
            className={cn(
              'px-3 py-1 text-sm rounded-r-md',
              localUnit === 'inch'
                ? 'bg-primary text-primary-foreground'
                : 'bg-background text-foreground hover:bg-gray-100'
            )}
          >
            in
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
        {filteredMeasurements.map(renderMeasurementInput)}
      </div>

      {/* Visual Guide */}
      <div className="rounded-lg bg-gray-50 dark:bg-gray-900 p-4">
        <h4 className="mb-2 text-sm font-medium">How to Measure</h4>
        <ul className="space-y-1 text-sm text-gray-600 dark:text-gray-400">
          <li>• Use a flexible measuring tape</li>
          <li>• Stand straight and relaxed</li>
          <li>• Don't pull the tape too tight</li>
          <li>• Measure over undergarments for accuracy</li>
        </ul>
      </div>
    </div>
  )

  if (showCard) {
    return (
      <div ref={ref} className={cn("rounded-lg border bg-card text-card-foreground shadow-sm", className)} {...props}>
        <div className="p-6">
          {content}
        </div>
      </div>
    )
  }

  return (
    <div ref={ref} className={cn("space-y-4", className)} {...props}>
      {content}
    </div>
  )
})

MeasurementsInput.displayName = "MeasurementsInput"